import React from "react";
import { gql, useQuery } from "@apollo/client";

import { CallToAction } from "src/components/cta/cta_helpers";
import { Error } from "src/components/error/error";
import { Loading } from "src/components/loading/loading";
import { StepIndicator } from "src/components/step_indicator/step_indicator";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle } from "src/components/typography/typography";
import { Viewer } from "src/generated/types";

import { ConstituentGroupsForm } from "./forms/constituent_groups";
import { ConstituentGroupsCombinedForm } from "./forms/constituent_groups_combined";
import "./constituent_groups.scss";

/*
 * GraphQL
 */

const REPORTS_CONSTITUENTS_GROUPS_QUERY = gql`
    query ReportsConstituentGroups {
        viewer {
            user {
                id
                school {
                    id
                    currentReports {
                        edges {
                            node {
                                id
                                constituentGroups
                                status
                                type
                            }
                        }
                    }
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloQueryData {
    viewer: Viewer;
}

/*
 * Main
 */

export const ConstituentGroupsPage = (): React.ReactElement => {
    const { data, error, loading } = useQuery<ApolloQueryData, Empty>(REPORTS_CONSTITUENTS_GROUPS_QUERY);

    if (loading) return <Loading />;
    if (error) return <Error />;

    const reports = data?.viewer.user.school?.currentReports.edges;

    const fsciReport = reports?.find((edge) => edge?.node?.type === "fsci")?.node;
    const ffiReport = reports?.find((edge) => edge?.node?.type === "ffi")?.node;

    if (!fsciReport && !ffiReport) {
        return (
            <div className="main-page-wrap short">
                <div className="center">
                    <Error message="There is a problem with your account. Please contact an Admin so they can resolve the issue." />
                </div>
            </div>
        );
    } else {
        const isCombinedReport = fsciReport && ffiReport;
        return (
            <div className="main-page-wrap short">
                <div className="center">
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                    <StepIndicator report={fsciReport || ffiReport!} />
                    <Separator />
                    <div>
                        <div className="title-wrap">
                            <PageTitle>Constituent Group Sizes</PageTitle>
                            <Body>(Current)</Body>
                        </div>
                    </div>
                    {isCombinedReport && (
                        <ConstituentGroupsCombinedForm
                            reports={[fsciReport, ffiReport]}
                            schoolId={data?.viewer.user.school?.id}
                        />
                    )}
                    {!isCombinedReport && fsciReport && (
                        <ConstituentGroupsForm report={fsciReport} schoolId={data?.viewer.user.school?.id} />
                    )}
                    {!isCombinedReport && ffiReport && (
                        <ConstituentGroupsForm report={ffiReport} schoolId={data?.viewer.user.school?.id} />
                    )}
                    <Separator />
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                    <CallToAction currentReport={fsciReport || ffiReport!} schoolId={data?.viewer.user.school?.id} />
                </div>
            </div>
        );
    }
};
